import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import { DataGridPro, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarQuickFilter } from '@mui/x-data-grid-pro';
import { Add, Delete, Refresh, Edit, ContentCopy } from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';

const REFRESH_MIN = 1;
const REFRESH_MAX = 86400;

function useApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchKeys = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKeys(res.data || []);
    } catch (err) {
      setError('Failed to load API keys.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createKey = async (payload) => {
    const token = localStorage.getItem('token');
    const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/api-keys`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  };

  const updateKey = async (id, payload) => {
    const token = localStorage.getItem('token');
    await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/admin/api-keys/${id}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const deleteKey = async (id) => {
    const token = localStorage.getItem('token');
    await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/admin/api-keys/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  return { keys, loading, error, fetchKeys, createKey, updateKey, deleteKey };
}

export default function APIKeys() {
  const { keys, loading, error, fetchKeys, createKey, updateKey, deleteKey } = useApiKeys();
  const [databases, setDatabases] = useState([]);
  const [density, setDensity] = useState('standard');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDb, setNewKeyDb] = useState('');
  const [newKeyRefresh, setNewKeyRefresh] = useState(60);
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [createdToken, setCreatedToken] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  const resetCreateState = () => {
    setNewKeyName('');
    setNewKeyDb('');
    setNewKeyRefresh(60);
    setNewKeyExpiry('');
    setCreatedToken('');
  };

  useEffect(() => {
    fetchKeys();
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/databases`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // filter out postgres/user_management
        const filtered = res.data.filter((db) => !['postgres', 'user_management'].includes(db.datname));
        setDatabases(filtered);
      } catch (err) {
        console.error('Failed to load databases', err);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const handleCreate = async () => {
    if (!newKeyName.trim() || !newKeyDb.trim()) {
      showToast('Name and database are required.', 'warning');
      return;
    }
    if (newKeyRefresh < REFRESH_MIN || newKeyRefresh > REFRESH_MAX) {
      showToast(`Refresh rate must be between ${REFRESH_MIN} and ${REFRESH_MAX} seconds.`, 'warning');
      return;
    }
    try {
      const payload = {
        name: newKeyName.trim(),
        database_name: newKeyDb,
        refresh_rate_seconds: newKeyRefresh,
        expires_at: newKeyExpiry || null,
      };
      const res = await createKey(payload);
      setCreatedToken(res.token || '');
      showToast('API key created. Copy the token now — it will not be shown again.');
      await fetchKeys();
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Failed to create API key.', 'error');
    }
  };

  const handleUpdate = async () => {
    if (!editingKey) return;
    try {
      const payload = {
        name: editingKey.name,
        refresh_rate_seconds: editingKey.refresh_rate_seconds,
        expires_at: editingKey.expires_at,
        revoked: editingKey.revoked,
      };
      await updateKey(editingKey.id, payload);
      showToast('API key updated.');
      setEditDialogOpen(false);
      setEditingKey(null);
      await fetchKeys();
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Failed to update API key.', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this API key?')) return;
    try {
      await deleteKey(id);
      showToast('API key deleted.');
      await fetchKeys();
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Failed to delete API key.', 'error');
    }
  };

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1.2, minWidth: 200 },
    { field: 'database_name', headerName: 'Database', flex: 0.9, minWidth: 150 },
    {
      field: 'refresh_rate_seconds',
      headerName: 'Refresh (s)',
      width: 130,
      renderCell: (params) => params.row.refresh_rate_seconds,
    },
    {
      field: 'expires_at',
      headerName: 'Expires',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) =>
        params.value ? dayjs(params.value).format('DD MMM YYYY') : 'No expiry',
    },
    {
      field: 'revoked',
      headerName: 'Status',
      width: 120,
      renderCell: (params) =>
        params.row.revoked ? (
          <Chip label="Revoked" color="error" size="small" />
        ) : (
          <Chip label="Active" color="success" size="small" />
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => {
              setEditingKey({ ...params.row });
              setEditDialogOpen(true);
            }}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <Box sx={{ display: 'flex', flex: '1 1 480px', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        <Button
          startIcon={<Refresh />}
          onClick={fetchKeys}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            size="small"
            onClick={() => {
              resetCreateState();
              setCreateDialogOpen(true);
            }}
            sx={{ textTransform: 'none' }}
          >
            New API Key
          </Button>
      </Box>
      <GridToolbarQuickFilter debounceMs={300} sx={{ ml: { xs: 0, md: 'auto' }, minWidth: { xs: '100%', sm: 220 } }} />
    </GridToolbarContainer>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 'none', mx: 0, flex: 1, minHeight: 520 }}>
      {error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchKeys}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Paper
        className="admin-table-card"
        elevation={0}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          border: '1px solid rgba(15,17,21,0.08)',
          boxShadow: 'none',
        }}
      >
        <Divider />
        <Box sx={{ flex: 1, minHeight: 360 }}>
          <DataGridPro
            rows={keys}
            columns={columns}
            density={density}
            loading={loading}
            pagination
            getRowId={(row) => row.id}
            onDensityChange={(newDensity) => setDensity(newDensity)}
            slots={{ toolbar: CustomToolbar }}
            sx={{
              height: '100%',
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                background: 'linear-gradient(135deg, rgba(11,13,18,0.04), rgba(17,22,34,0.02))',
                borderBottom: '1px solid rgba(15,17,21,0.10)',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(15,17,21,0.06)',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Create dialog */}
      <Dialog
        open={createDialogOpen}
        disableEscapeKeyDown={Boolean(createdToken)}
        onClose={(_event, reason) => {
          if (createdToken && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
          setCreateDialogOpen(false);
          resetCreateState();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            required
            fullWidth
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Database"
            select
            required
            fullWidth
            value={newKeyDb}
            onChange={(e) => setNewKeyDb(e.target.value)}
            sx={{ mt: 2 }}
          >
            {databases.map((db) => (
              <MenuItem key={db.datname} value={db.datname}>
                {db.datname}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Refresh rate (seconds)"
            type="number"
            required
            fullWidth
            value={newKeyRefresh}
            onChange={(e) => setNewKeyRefresh(Number(e.target.value))}
            sx={{ mt: 2 }}
            helperText={`Between ${REFRESH_MIN} and ${REFRESH_MAX}`}
          />
          <TextField
            label="Expires at"
            type="date"
            required
            fullWidth
            value={newKeyExpiry}
            onChange={(e) => setNewKeyExpiry(e.target.value)}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          {createdToken && (
            <Box
              role="status"
              aria-live="polite"
              sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'warning.main', backgroundColor: 'warning.light', borderRadius: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: 600 }}>Token:</Typography>
                <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{createdToken}</Typography>
                <Tooltip title="Copy token">
                  <IconButton
                    size="small"
                    aria-label="Copy API token"
                    onClick={async () => {
                      await navigator.clipboard.writeText(createdToken);
                      setSnackbar({ open: true, message: 'API token copied.', severity: 'success' });
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography sx={{ fontSize: 12, color: 'rgba(15,17,21,0.62)', mt: 0.5 }}>
                This token is shown only once. Copy and store it securely.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {createdToken ? (
            <Button
              variant="contained"
              onClick={() => {
                setCreateDialogOpen(false);
                resetCreateState();
              }}
            >
              I’ve saved the token
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setCreateDialogOpen(false);
                  resetCreateState();
                }}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={handleCreate}>Create</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            value={editingKey?.name || ''}
            onChange={(e) => setEditingKey((prev) => ({ ...prev, name: e.target.value }))}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Refresh rate (seconds)"
            type="number"
            fullWidth
            value={editingKey?.refresh_rate_seconds || 60}
            onChange={(e) => setEditingKey((prev) => ({ ...prev, refresh_rate_seconds: Number(e.target.value) }))}
            sx={{ mt: 2 }}
            helperText={`Between ${REFRESH_MIN} and ${REFRESH_MAX}`}
          />
          <TextField
            label="Expires at"
            type="date"
            fullWidth
            value={editingKey?.expires_at ? editingKey.expires_at.substring(0, 10) : ''}
            onChange={(e) => setEditingKey((prev) => ({ ...prev, expires_at: e.target.value }))}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Status"
            select
            fullWidth
            value={editingKey?.revoked ? 'revoked' : 'active'}
            onChange={(e) => setEditingKey((prev) => ({ ...prev, revoked: e.target.value === 'revoked' }))}
            sx={{ mt: 2 }}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="revoked">Revoked</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity || 'success'} variant="filled" onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
